import { makeStyles } from '@material-ui/core';
import ListItem, { ListItemProps } from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip, { TooltipProps } from '@material-ui/core/Tooltip';
import classnames from 'classnames';
import React, { cloneElement, FC, ReactElement, useCallback } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { colors } from 'theme';

const useStyles = makeStyles(theme => ({
  groupSelectedMenuItemLink: {
    color: colors.grey900,
  },
  group: {
    boxShadow: `inset 6px 0px 0px 0px ${colors.grey100}`,
  },
  groupSelectedMenuItemIcon: {
    color: colors.grey900,
  },
  notSelectedMenuItemIcon: {
    color: '#000',
  },
  notSelectedMenuItemText: {
    color: '#000',
  },
  selectedMenuItemLink: {
    color: theme.palette.primary.main,
    backgroundColor: colors.grey050,
    boxShadow: `inset 6px 0px 0px 0px ${theme.palette.primary.main}`,
  },
  selectedMenuItemIcon: {
    color: theme.palette.primary.main,
  },
  selectedMenuItemText: {
    '& > *': {
      fontWeight: 600,
    },
  },
}));

const MenuItemLink: FC<MenuItemLinkProps> = props => {
  const { primaryText, leftIcon, to, group, urlMatch, exact, sidebarIsOpen, tooltipProps, ...rest } = props;

  const classes = useStyles();
  const match = useRouteMatch({
    path: urlMatch ?? to,
    exact,
  });
  const matchGroup = useRouteMatch({
    path: group,
    exact: false,
  });
  const history = useHistory();

  const navigate = useCallback(() => {
    history.push(to);
  }, [history, to]);

  const renderMenuItem = (sidebarIsOpen: boolean) => {
    return (
      <ListItem
        onClick={navigate}
        className={classnames({
          [classes.group]: Boolean(group),
          [classes.groupSelectedMenuItemLink]: matchGroup,
          [classes.selectedMenuItemLink]: match,
        })}
        {...rest}
      >
        <ListItemIcon
          className={classnames({
            [classes.selectedMenuItemIcon]: match,
            [classes.groupSelectedMenuItemIcon]: matchGroup,
            [classes.notSelectedMenuItemIcon]: !match && !matchGroup,
          })}
        >
          {cloneElement(leftIcon, {
            titleaccess: primaryText,
          })}
        </ListItemIcon>
        {sidebarIsOpen ? (
          <ListItemText
            primary={primaryText}
            className={classnames({
              [classes.selectedMenuItemText]: match,
              [classes.notSelectedMenuItemText]: !match && !matchGroup,
            })}
          />
        ) : (
          ''
        )}
      </ListItem>
    );
  };

  if (sidebarIsOpen) {
    return renderMenuItem(true);
  }

  return (
    <Tooltip title={primaryText} placement="right" {...tooltipProps}>
      {renderMenuItem(false)}
    </Tooltip>
  );
};

interface Props {
  leftIcon: ReactElement;
  primaryText: string;
  sidebarIsOpen: boolean;
  to: string;
  group?: string;
  exact?: boolean;
  urlMatch?: string | string[];
  tooltipProps?: TooltipProps;
}

export type MenuItemLinkProps = Props & ListItemProps<'div', { button: true }> & { button: true };

export default React.memo(MenuItemLink);
